import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { AiAPI , apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Eraser, Upload, Download, Loader2, Flame, ImageIcon } from "lucide-react";

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res((fr.result || "").toString().split(",")[1]);
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl; a.download = filename; a.click();
}

export default function WatermarkRemoverPage() {
  const { user, usage, refreshUsage } = useAuth();
  const { toast } = useToast();
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const inputRef = React.useRef(null);

  const onFile = async (f) => {
    if (!f) return;
    setFile(f); setResult(null);
    const b64 = await fileToBase64(f);
    setPreview(`data:${f.type};base64,${b64}`);
  };

  const run = async () => {
    if (!user) { toast({ title: "Sign in first", description: "Create a free account to use AI tools." }); return; }
    if (!file) { toast({ title: "Upload an image", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      const r = await AiAPI.watermarkRemove(b64);
      setResult(`data:${r.data.mime_type};base64,${r.data.image_base64}`);
      await refreshUsage();
      toast({ title: "Watermark removed" });
    } catch (err) {
      toast({ title: "Removal failed", description: apiErrorMessage(err, ""), variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="grain">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="chip"><Eraser className="h-3 w-3 text-[#4aa4ff]" /> Watermark Remover</span>
            <h1 className="display-font mt-3 text-3xl font-bold sm:text-4xl">Wipe watermarks in <span className="ember-text">one click</span>.</h1>
            <p className="mt-1 text-sm text-muted-foreground">Logos, stamps, timestamps, URLs, stock overlays — removed and reconstructed cleanly.</p>
          </div>
          {usage && (<div className="chip"><span className="h-1.5 w-1.5 rounded-full bg-[#4aa4ff]" /> {usage.pro ? "Pro · Unlimited" : `${usage.remaining} left this month`}</div>)}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold">Original</div>
              <Button variant="outline" onClick={() => inputRef.current?.click()} className="gap-2"><Upload className="h-4 w-4" /> Upload</Button>
              <input ref={inputRef} type="file" hidden accept="image/*" onChange={(e) => onFile(e.target.files?.[0])} />
            </div>
            <div
              className="flex min-h-[380px] items-center justify-center rounded-xl bg-white p-4"
              onDragOver={(e) => e.preventDefault()}
              onDrop={async (e) => { e.preventDefault(); await onFile(e.dataTransfer.files?.[0]); }}
            >
              {preview ? <img src={preview} alt="preview" className="max-h-[420px] max-w-full rounded-md" /> : (
                <div className="text-center text-muted-foreground">
                  <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-[#4aa4ff]"><ImageIcon className="h-5 w-5" /></div>
                  <div>Drop an image or click Upload</div>
                </div>
              )}
            </div>
            <Button className="btn-ember mt-4 h-11 w-full gap-2 font-semibold" onClick={run} disabled={busy || !file}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eraser className="h-4 w-4" />} Remove watermark
            </Button>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-3 text-sm font-semibold">Cleaned</div>
            <div className="flex min-h-[380px] items-center justify-center rounded-xl bg-white p-4">
              {busy ? (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin text-[#4aa4ff]" /> Removing…
                </div>
              ) : result ? (
                <img src={result} alt="result" className="max-h-[420px] max-w-full rounded-md" />
              ) : (
                <div className="text-center text-muted-foreground">Your cleaned image will appear here.</div>
              )}
            </div>
            {result && (
              <div className="mt-4 flex gap-3">
                <Button className="btn-ember h-11 gap-2 font-semibold" onClick={() => downloadDataUrl(result, `no_watermark_${Date.now()}.png`)}><Download className="h-4 w-4" /> Download</Button>
                <Button variant="outline" onClick={() => { setResult(null); setPreview(null); setFile(null); }}>New image</Button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground">
          <Flame className="h-4 w-4 text-[#4aa4ff]" /> Only use this tool on images you own or have permission to modify.
        </div>
      </section>
      <Footer />
    </div>
  );
}
